/**
 * Extracts a display name for a conversation.
 * - Group conversations: use the name field
 * - Direct conversations: use the other participant's name
 */
export function getConversationDisplayName(conversation, currentUserId) {
  if (!conversation) return 'Unknown';

  // Group conversations have an explicit name
  if (conversation.type === 'group' && conversation.name) {
    return conversation.name;
  }

  // Direct conversations: find the other participant
  const otherParticipant = (conversation.participants || []).find(
    (p) => `${p.user_id || p.userId}` !== `${currentUserId}`,
  );

  if (otherParticipant) {
    const participantId =
      otherParticipant.user_id ??
      otherParticipant.userId ??
      otherParticipant.user?.id ??
      otherParticipant.id;
    return (
      otherParticipant.name ||
      otherParticipant.username ||
      otherParticipant.email ||
      `User ${participantId}`
    );
  }

  // user object not populated — fall back to user_id
  if (otherParticipant?.user_id || otherParticipant?.userId) {
    return `User ${otherParticipant.user_id || otherParticipant.userId}`;
  }

  return conversation.name || 'Unknown Conversation';
}

/**
 * Gets conversation subtitle for display.
 * - Group: participant count
 * - Direct: empty or user status
 */
export function getConversationSubtitle(conversation) {
  if (!conversation) return '';

  if (conversation.type === 'group') {
    const count = (conversation.participants || []).filter((p) => !p.left_at).length;
    return `${count} ${count === 1 ? 'participant' : 'participants'}`;
  }

  return '';
}

/**
 * Gets the last message preview text.
 */
export function getLastMessagePreview(conversation, currentUserId = null) {
  const lastMsg = conversation.last_message;
  if (!lastMsg) return 'No messages yet';

  if (lastMsg.deleted_at) return 'Message deleted';

  if (lastMsg.content_type === 'image') {
    return lastMsg.content ? `📷 ${lastMsg.content}` : '📷 Image';
  }

  if (lastMsg.content_type === 'video') {
    return lastMsg.content ? `🎥 ${lastMsg.content}` : '🎥 Video';
  }

  const text = typeof lastMsg.content === 'string' ? lastMsg.content.trim() : '';
  if (text) return text;

  // Message exists but has no previewable plaintext (for example, encrypted payload only).
  if (
    lastMsg.id != null ||
    lastMsg.sent_at ||
    lastMsg.encryptedContent ||
    lastMsg.encrypted_content
  ) {
    const senderId = lastMsg.sender_id ?? lastMsg.senderId ?? lastMsg.sender?.id ?? null;
    const isOwnMessage =
      currentUserId != null && senderId != null && `${senderId}` === `${currentUserId}`;
    return isOwnMessage ? 'You sent a message' : 'New message';
  }

  return 'No messages yet';
}

/**
 * Formats an ISO timestamp for display.
 */
export function formatMessageTime(isoString) {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    // Format as date
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Formats an ISO timestamp as a compact relative age for conversation rows.
 */
export function formatCompactRelativeTime(isoString, nowMs = Date.now()) {
  if (!isoString) return '';

  try {
    const timestampMs = new Date(isoString).getTime();
    if (!Number.isFinite(timestampMs)) return '';

    const diffMins = Math.max(0, Math.floor((nowMs - timestampMs) / 60000));
    if (diffMins === 0) return 'Now';
    if (diffMins < 60) return `${diffMins}m`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;

    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 5) return `${diffWeeks}w`;

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths}mo`;

    return `${Math.floor(diffDays / 365)}y`;
  } catch {
    return '';
  }
}

export function getConversationActivityTimestamp(conversation) {
  return (
    conversation?.activityTimestamp ??
    conversation?.activity_timestamp ??
    conversation?.last_message?.sent_at ??
    conversation?.last_message?.sentAt ??
    conversation?.lastMessageAt ??
    conversation?.updated_at ??
    conversation?.created_at ??
    null
  );
}

/**
 * Formats time for clock display (HH:MM).
 */
export function formatClock(isoString) {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Gets the sender's display name from a message.
 */
export function getMessageSenderName(message, participants = []) {
  if (!message) return 'Unknown';

  const sender = participants.find(
    (p) => `${p.userId ?? p.user_id ?? p.user?.id}` === `${message.sender_id}`,
  );

  if (sender) {
    const senderId = sender.id ?? sender.userId ?? sender.user_id ?? sender.user?.id;
    return sender.name || sender.username || sender.email || `User ${senderId}`;
  }

  return `User ${message.sender_id}`;
}

/**
 * Checks if a message was sent by the current user.
 */
export function isOwnMessage(message, currentUserId) {
  return `${message?.sender_id}` === `${currentUserId}`;
}

/**
 * Gets media URL for display from message metadata.
 */
export function getMediaUrl(message) {
  return message?.metadata?.file?.url || null;
}

/**
 * Gets media filename for display.
 */
export function getMediaFilename(message) {
  return message?.metadata?.file?.originalname || message?.metadata?.file?.filename || null;
}

/**
 * Checks if the current user can perform an action based on their role.
 */
export function canPerformAction(action, userRole) {
  const permissions = {
    owner: ['send', 'add', 'remove', 'rename', 'assign', 'transfer'],
    admin: ['send', 'add', 'remove-standard', 'rename', 'assign-standard'],
    standard: ['send', 'leave'],
  };

  return (permissions[userRole] || []).some((perm) => perm === action || perm.startsWith(action));
}

/**
 * Gets the current user's role in a conversation.
 */
export function getUserRole(conversation, currentUserId) {
  if (!conversation?.participants) return null;

  const participant = conversation.participants.find(
    (p) => `${p.user_id ?? p.userId ?? p.user?.id}` === `${currentUserId}`,
  );
  return participant?.role || null;
}
